import React, { ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames/bind'

import Block from 'app/components/common/block/block.component'
import { Button } from 'app/components/common'
import stylesModule from 'app/components/modal/modal.module.scss'
import { CloseModalIcon } from 'app/components/icons'

import useOutsideClick from 'app/hooks/use-outside-click.hook'
import useTabulationLoop from 'app/hooks/use-tabulation-loop.hook'
import KEY_VALUES from 'app/constants/key-value.constants'

const cx = classNames.bind(stylesModule)

type ClassNameList = {
  main?: string
  wrapper?: string
  closeButton?: string
  backButton?: string
}

type ModalProps = {
  selectors: string
  children: ReactNode
  classNameList?: ClassNameList
  close: () => void
  blockClose?: boolean
  modalTestId?: string
  ariaLabel?: string
}

function Modal({
  children,
  close,
  selectors,
  classNameList = {},
  blockClose = false,
  modalTestId,
  ariaLabel,
}: ModalProps) {
  const modalRef = useRef<HTMLElement>(null)

  useOutsideClick({
    targetRef: modalRef,
    callback: close,
    closeOnTarget: true,
    blockClick: blockClose,
  })

  useTabulationLoop({
    targetRef: modalRef,
    selectors,
  })

  useEffect(() => {
    function handleKeyPress(e: KeyboardEvent) {
      if (e.key === KEY_VALUES.ESCAPE && !blockClose) {
        close()
      }
    }

    window.addEventListener('keydown', handleKeyPress)

    return function cleanup() {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [close, selectors, children, blockClose])

  return createPortal(
    <Block
      data-testid={modalTestId}
      className={cx('modal')}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      <Block
        ref={modalRef}
        type="section"
        className={cx('modal__wrapper', classNameList.wrapper)}
      >
        <Block type="section" className={cx('modal__main', classNameList.main)}>
          {children}
        </Block>
        {!blockClose && (
          <Button
            onClick={close}
            className={cx(
              'modal__button',
              'modal__button--close',
              classNameList.closeButton
            )}
            aria-label="close"
          >
            <CloseModalIcon size={59} />
          </Button>
        )}
      </Block>
    </Block>,
    document.body
  )
}

export default Modal
